// © 2026 Buddy Media LLC. All rights reserved.
//
// Cross-origin fetch helper. Reads VITE_BW_BACKEND_URL at build time;
// in dev, vite.config.ts proxies /api → backend so the same call
// works without CORS.
//
// The bearer comes from the in-memory authStore — never localStorage.
// Per the MCP_ADMIN_PROXY_CONTRACT (backend doc), the backend's
// /api/admin/* routes accept Bearer tokens and Are CORS-friendly via
// CORS_ALLOW_ORIGINS env.

import { useAuthStore } from "@/store/authStore";

const BACKEND_URL =
  (import.meta.env.VITE_BW_BACKEND_URL as string | undefined) ?? "";

// T-678 Phase 3 — MCP backend hostname. Pages migrated from BW to
// MCP call mcpFetch instead of apiFetch. Defaults to dev-tier MCP.
const MCP_URL =
  (import.meta.env.VITE_MCP_BACKEND_URL as string | undefined) ??
  "https://dev-mcp.buddymedia.online";

export class ApiError extends Error {
  status: number;
  detail?: unknown;
  /** Alias for `detail` — source pages were written against the
   * dev-buddywatch-frontend-desktop ApiError which used `data`. */
  data?: unknown;

  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
    this.data = detail;
  }
}

/** Best-effort human-readable error string for catch-handlers. */
export function describeApiError(err: unknown, fallback = "Request failed"): string {
  if (err instanceof ApiError) {
    const d = err.detail as { detail?: unknown; message?: string } | undefined;
    if (d) {
      if (typeof d.detail === "string") return d.detail;
      if (typeof d.message === "string") return d.message;
      if (typeof d === "string") return d;
    }
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const bearer = useAuthStore.getState().bearer;
  // In dev when no BACKEND_URL is set, vite proxies /api to backend.
  const base = BACKEND_URL || "";
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);

  const response = await fetch(url, { ...init, headers });
  if (response.status === 401) {
    // Clear stale bearer; the route gate will redirect to /login.
    useAuthStore.getState().setBearer(null);
    throw new ApiError("Unauthorized", 401);
  }
  if (!response.ok) {
    let detail: unknown = undefined;
    try {
      detail = await response.json();
    } catch {
      try {
        detail = await response.text();
      } catch {
        // ignore
      }
    }
    throw new ApiError(`HTTP ${response.status}`, response.status, detail);
  }
  if (response.status === 204) return null as T;
  const text = await response.text();
  if (!text) return null as T;
  return JSON.parse(text) as T;
}

/** T-678 Phase 3 — call the MCP backend (dev-mcp.buddymedia.online).
 * Same shape + bearer rules as apiFetch; only the base URL differs.
 * Bearer must be a valid magic-link admin JWT or a cluster-mesh JWT
 * (T-612). On 401 the bearer is cleared and the route gate redirects
 * to /login (or /admin-login for the magic-link path). */
export async function mcpFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const bearer = useAuthStore.getState().bearer;
  const url = path.startsWith("http") ? path : `${MCP_URL}${path}`;
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);

  const response = await fetch(url, { ...init, headers });
  if (response.status === 401) {
    useAuthStore.getState().setBearer(null);
    throw new ApiError("Unauthorized (MCP)", 401);
  }
  if (!response.ok) {
    let detail: unknown = undefined;
    try {
      detail = await response.json();
    } catch {
      try {
        detail = await response.text();
      } catch {
        // ignore
      }
    }
    throw new ApiError(`HTTP ${response.status}`, response.status, detail);
  }
  if (response.status === 204) return null as T;
  const text = await response.text();
  if (!text) return null as T;
  return JSON.parse(text) as T;
}
